import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { H2 } from 'UIComponents/elements/headings';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { userInfoSync } from 'hub-http/userInfo';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
// @ts-expect-error Untyped module
import { getContentTypeSlug } from 'ContentEditorUI/redux/selectors/contentTypeSelector';
import { getHardSavedContentModelIsPublishedNotScheduled, getHardSavedContentModelIsScheduled, getHardSavedContentModelScheduledUpdateDate } from 'ContentEditorUI/redux/selectors/hardSavedContentModelSelectors';
import { getPublishDate, getContentModelPageExpiryDate } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { updatePublishDate, updatePublishImmediately
// @ts-expect-error Untyped module
} from 'ContentEditorUI/redux/actions/publishActions';
import { updateExpiryDate, updateExpiryEnabled, updateExpiryRedirectUrl
// @ts-expect-error Untyped module
} from 'ContentEditorUI/redux/actions/baseContentModelActions';
import { getContentModelPageExpiryRedirectId, getContentModelPageExpiryRedirectUrl, getContentModelPageExpiryEnabled } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import UILink from 'UIComponents/link/UILink';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import CurrentlyScheduledCardInfo from './schedulingOptions/CurrentlyScheduledCardInfo';
import SchedulingUnpublishControls from './schedulingOptions/SchedulingUnpublishControls';
import SchedulingPublishControls from './schedulingOptions/SchedulingPublishControls';
import NothingScheduledControls from './schedulingOptions/NothingScheduledControls';
import { SchedulingSteps } from './schedulingOptions/constants';
import SchedulingTabs from './schedulingOptions/SchedulingTabs';
import { createSimpleDateFromMomentTsIfExists, getDateTimeInMinutes } from '../../../utils/publishingTimeUtils';
import useContentCrmObject from '../hooks/useContentCrmObject';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue } from 'content-management-data-lib';
import { PublishTypes } from '../../../constants/PublishTypes';
import { getIsPage } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { toMomentPortalTz } from 'foundations-components/transitional/utils/SimpleDate';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useCurrentStepFromContentCrmObject = () => {
  const {
    isContentCrmObjectScheduled,
    isContentCrmObjectPublished,
    isContentCrmObjectScheduledToBeUnpublished,
    isContentCrmObjectPublishedWithScheduledUpdate
  } = useContentCrmObject();
  const [currentStep, setCurrentStep] = useState({
    schedulePublish: isContentCrmObjectScheduled || isContentCrmObjectPublishedWithScheduledUpdate ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED,
    scheduleUnpublish: isContentCrmObjectScheduledToBeUnpublished ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED
  });
  const actionTypeBodyInfoKey = useMemo(() => ({
    schedulePublish: isContentCrmObjectPublished ? 'scheduleUpdate' : 'schedulePublish',
    scheduleUnpublish: 'scheduleUnpublish'
  }), [isContentCrmObjectPublished]);
  return {
    currentStep,
    updateCurrentStep: (tabType, nextStep) => {
      setCurrentStep(Object.assign({}, currentStep, {
        [tabType]: nextStep
      }));
    },
    actionTypeBodyInfoKey
  };
};
const useCurrentStepFromLegacyObject = () => {
  const isScheduled = useSelector(getHardSavedContentModelIsScheduled);
  const isPublished = useSelector(getHardSavedContentModelIsPublishedNotScheduled);
  const isPageExpiryEnabled = useSelector(getContentModelPageExpiryEnabled);
  const scheduledUpdateDate = useSelector(getHardSavedContentModelScheduledUpdateDate);
  const [currentStep, setCurrentStep] = useState({
    schedulePublish: isScheduled || isPublished && !!scheduledUpdateDate ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED,
    scheduleUnpublish: isPageExpiryEnabled ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED
  });
  const actionTypeBodyInfoKey = useMemo(() => ({
    schedulePublish: isPublished ? 'scheduleUpdate' : 'schedulePublish',
    scheduleUnpublish: 'scheduleUnpublish'
  }), [isPublished]);
  return {
    currentStep,
    updateCurrentStep: (tabType, nextStep) => {
      setCurrentStep(Object.assign({}, currentStep, {
        [tabType]: nextStep
      }));
    },
    actionTypeBodyInfoKey
  };
};
const useCurrentStep = () => {
  const {
    canScheduleWithContentCrmObject
  } = useContentCrmObject();
  const useStep = canScheduleWithContentCrmObject ? useCurrentStepFromContentCrmObject : useCurrentStepFromLegacyObject;
  return useStep();
};
const getTimeInMillis = ({
  date,
  minutes
}) => {
  if (date === null || date === undefined || minutes === undefined) {
    return undefined;
  }
  const unixTime = toMomentPortalTz(date).valueOf();
  const result = unixTime + minutes * 60000;
  return result;
};
const useSchedulingOptionsFromContentCrmObject = () => {
  const {
    contentCrmObject,
    isContentCrmObjectPublished,
    isContentCrmObjectScheduled,
    isContentCrmObjectPublishedWithScheduledUpdate
  } = useContentCrmObject();
  const {
    contentCrmObjectPublishDate,
    contentCrmObjectScheduledPublishDate,
    contentCrmObjectScheduledUpdateDate,
    contentCrmObjectScheduledUnpublishDate,
    contentCrmObjectUnpublishExpiryUrl
  } = useMemo(() => {
    if (contentCrmObject) {
      return {
        contentCrmObjectPublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISHED_AT),
        contentCrmObjectScheduledPublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE),
        contentCrmObjectScheduledUpdateDate: isContentCrmObjectPublishedWithScheduledUpdate ? getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_PUBLISH_DATE) : undefined,
        contentCrmObjectScheduledUnpublishDate: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.SCHEDULED_UNPUBLISH_DATE),
        contentCrmObjectUnpublishExpiryUrl: getContentPropertyValue(contentCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.UNPUBLISH_EXPIRY_URL)
      };
    }
    return {};
  }, [contentCrmObject, isContentCrmObjectPublishedWithScheduledUpdate]);
  const [scheduledPublishDate, setScheduledPublishDate] = useState(() => {
    if (isContentCrmObjectPublishedWithScheduledUpdate) {
      return createSimpleDateFromMomentTsIfExists(contentCrmObjectScheduledUpdateDate);
    }
    if (isContentCrmObjectScheduled) {
      return createSimpleDateFromMomentTsIfExists(contentCrmObjectScheduledPublishDate);
    }
  });
  const [scheduledPublishTime, setScheduledPublishTime] = useState(() => {
    if (isContentCrmObjectPublishedWithScheduledUpdate) {
      return getDateTimeInMinutes(contentCrmObjectScheduledUpdateDate);
    }
    if (isContentCrmObjectScheduled) {
      return getDateTimeInMinutes(contentCrmObjectScheduledPublishDate);
    }
  });
  const [scheduledUnpublishDate, setScheduledUnpublishDate] = useState(() => {
    return createSimpleDateFromMomentTsIfExists(contentCrmObjectScheduledUnpublishDate);
  });
  const [scheduledUnpublishTime, setScheduledUnpublishTime] = useState(() => {
    return getDateTimeInMinutes(contentCrmObjectScheduledUnpublishDate);
  });
  const scheduledPublishDateTime = getTimeInMillis({
    date: scheduledPublishDate,
    minutes: scheduledPublishTime
  });
  const scheduledUnpublishDateTime = getTimeInMillis({
    date: scheduledUnpublishDate,
    minutes: scheduledUnpublishTime
  });

  // FIXME: These don't exist on ContentCrmObject, but needed for pages object
  const [pageExpiryRedirectUrl, setPageExpiryRedirectUrl] = useState(undefined);
  const [pageExpiryRedirectId, setPageExpiryRedirectId] = useState(undefined);
  const [unpublishExpiryUrl, setUnpublishExpiryUrl] = useState(contentCrmObjectUnpublishExpiryUrl);
  const minimumDateTimeForUnpublish = useMemo(() => {
    if (isContentCrmObjectPublished) {
      const currentPublishDate = createSimpleDateFromMomentTsIfExists(contentCrmObjectPublishDate);
      const areSelectedDatesSame = currentPublishDate && scheduledUnpublishDate && currentPublishDate.year === scheduledUnpublishDate.year && currentPublishDate.month === scheduledUnpublishDate.month && currentPublishDate.date === scheduledUnpublishDate.date;
      return {
        date: currentPublishDate,
        time: areSelectedDatesSame ? getDateTimeInMinutes(contentCrmObjectPublishDate) : undefined
      };
    }
    if (isContentCrmObjectScheduled || scheduledPublishDateTime) {
      const currentScheduledPublishDate = createSimpleDateFromMomentTsIfExists(scheduledPublishDateTime);
      const areSelectedDatesSame = currentScheduledPublishDate && scheduledUnpublishDate && currentScheduledPublishDate.year === scheduledUnpublishDate.year && currentScheduledPublishDate.month === scheduledUnpublishDate.month && currentScheduledPublishDate.date === scheduledUnpublishDate.date;
      return {
        date: currentScheduledPublishDate,
        time: areSelectedDatesSame ? getDateTimeInMinutes(scheduledPublishDateTime) : undefined
      };
    }
  }, [contentCrmObjectPublishDate, isContentCrmObjectPublished, isContentCrmObjectScheduled, scheduledPublishDateTime, scheduledUnpublishDate]);
  return {
    scheduledPublishDate,
    scheduledPublishTime,
    setScheduledPublishDate,
    setScheduledPublishTime,
    scheduledPublishDateTime,
    scheduledUnpublishDate,
    scheduledUnpublishTime,
    setScheduledUnpublishDate,
    setScheduledUnpublishTime,
    scheduledUnpublishDateTime,
    /** @deprecated */
    pageExpiryRedirectUrl,
    /** @deprecated */
    pageExpiryRedirectId,
    /** @deprecated */
    setPageExpiryRedirectUrl,
    /** @deprecated */
    setPageExpiryRedirectId,
    unpublishExpiryUrl,
    setUnpublishExpiryUrl,
    dateTimeCopyOptions: {
      portalTimezone: userInfoSync().portal.timezone.id,
      scheduledTime: I18n.moment(scheduledPublishDateTime)
      // @ts-expect-error portalTz() isn't typed
      .portalTz().format('LLL'),
      unpublishTime: I18n.moment(scheduledUnpublishDateTime)
      // @ts-expect-error portalTz() isn't typed
      .portalTz().format('LLL')
    },
    minimumDateForUnpublish: minimumDateTimeForUnpublish === null || minimumDateTimeForUnpublish === void 0 ? void 0 : minimumDateTimeForUnpublish.date,
    minimumTimeForUnpublish: minimumDateTimeForUnpublish === null || minimumDateTimeForUnpublish === void 0 ? void 0 : minimumDateTimeForUnpublish.time
  };
};
const useSchedulingOptionsFromLegacyObject = () => {
  const publishDate = useSelector(getPublishDate);
  const isPublished = useSelector(getHardSavedContentModelIsPublishedNotScheduled);
  const scheduledUpdateDate = useSelector(getHardSavedContentModelScheduledUpdateDate);
  const isScheduled = useSelector(getHardSavedContentModelIsScheduled);
  const [scheduledPublishDate, setScheduledPublishDate] = useState(() => {
    if (isPublished && scheduledUpdateDate) {
      return createSimpleDateFromMomentTsIfExists(scheduledUpdateDate);
    }
    if (isScheduled) {
      return createSimpleDateFromMomentTsIfExists(publishDate);
    }
  });
  const [scheduledPublishTime, setScheduledPublishTime] = useState(() => {
    if (isPublished && scheduledUpdateDate) {
      return getDateTimeInMinutes(scheduledUpdateDate);
    }
    if (isScheduled) {
      return getDateTimeInMinutes(publishDate);
    }
  });
  const unpublishDate = useSelector(getContentModelPageExpiryDate);
  const [scheduledUnpublishDate, setScheduledUnpublishDate] = useState(() => {
    return createSimpleDateFromMomentTsIfExists(unpublishDate);
  });
  const [scheduledUnpublishTime, setScheduledUnpublishTime] = useState(() => {
    return getDateTimeInMinutes(unpublishDate);
  });
  const scheduledPublishDateTime = getTimeInMillis({
    date: scheduledPublishDate,
    minutes: scheduledPublishTime
  });
  const scheduledUnpublishDateTime = getTimeInMillis({
    date: scheduledUnpublishDate,
    minutes: scheduledUnpublishTime
  });
  const redirectId = useSelector(getContentModelPageExpiryRedirectId);
  const redirectUrl = useSelector(getContentModelPageExpiryRedirectUrl);
  const [pageExpiryRedirectUrl, setPageExpiryRedirectUrl] = useState(redirectUrl);
  const [pageExpiryRedirectId, setPageExpiryRedirectId] = useState(redirectId);
  const minimumDateTimeForUnpublish = useMemo(() => {
    if (isPublished) {
      const currentPublishDate = createSimpleDateFromMomentTsIfExists(publishDate);
      const areSelectedDatesSame = currentPublishDate && scheduledUnpublishDate && currentPublishDate.year === scheduledUnpublishDate.year && currentPublishDate.month === scheduledUnpublishDate.month && currentPublishDate.date === scheduledUnpublishDate.date;
      return {
        date: currentPublishDate,
        time: areSelectedDatesSame ? getDateTimeInMinutes(publishDate) : undefined
      };
    }
    if (isScheduled || scheduledPublishDateTime) {
      const currentScheduledPublishDate = createSimpleDateFromMomentTsIfExists(scheduledPublishDateTime);
      const areSelectedDatesSame = currentScheduledPublishDate && scheduledUnpublishDate && currentScheduledPublishDate.year === scheduledUnpublishDate.year && currentScheduledPublishDate.month === scheduledUnpublishDate.month && currentScheduledPublishDate.date === scheduledUnpublishDate.date;
      return {
        date: currentScheduledPublishDate,
        time: areSelectedDatesSame ? getDateTimeInMinutes(scheduledPublishDateTime) : undefined
      };
    }
  }, [isPublished, isScheduled, publishDate, scheduledPublishDateTime, scheduledUnpublishDate]);
  return {
    scheduledPublishDate,
    scheduledPublishTime,
    setScheduledPublishDate,
    setScheduledPublishTime,
    scheduledPublishDateTime,
    scheduledUnpublishDate,
    scheduledUnpublishTime,
    setScheduledUnpublishDate,
    setScheduledUnpublishTime,
    scheduledUnpublishDateTime,
    /** @deprecated */
    pageExpiryRedirectUrl,
    /** @deprecated */
    pageExpiryRedirectId,
    /** @deprecated */
    setPageExpiryRedirectUrl,
    /** @deprecated */
    setPageExpiryRedirectId,
    unpublishExpiryUrl: undefined,
    setUnpublishExpiryUrl: () => {},
    dateTimeCopyOptions: {
      portalTimezone: userInfoSync().portal.timezone.id,
      scheduledTime: I18n.moment(scheduledPublishDateTime)
      // @ts-expect-error portalTz() isn't typed
      .portalTz().format('LLL'),
      unpublishTime: I18n.moment(scheduledUnpublishDateTime)
      // @ts-expect-error portalTz() isn't typed
      .portalTz().format('LLL')
    },
    minimumDateForUnpublish: minimumDateTimeForUnpublish === null || minimumDateTimeForUnpublish === void 0 ? void 0 : minimumDateTimeForUnpublish.date,
    minimumTimeForUnpublish: minimumDateTimeForUnpublish === null || minimumDateTimeForUnpublish === void 0 ? void 0 : minimumDateTimeForUnpublish.time
  };
};
const useSchedulingOptions = () => {
  const {
    canScheduleWithContentCrmObject
  } = useContentCrmObject();
  const useOptions = canScheduleWithContentCrmObject ? useSchedulingOptionsFromContentCrmObject : useSchedulingOptionsFromLegacyObject;
  return useOptions();
};
const BaseSchedulingOptionsModal = ({
  allowBackdating = false,
  canScheduleUnpublish = false,
  publish,
  schedulePublishUnpublish,
  dashboardUrl,
  closeModal,
  openCancelScheduleConfirmModal,
  contentTypeHasSavedViews
}) => {
  const [currentTabType, setCurrentTabType] = useState('schedulePublish');
  const {
    currentStep,
    updateCurrentStep,
    actionTypeBodyInfoKey
  } = useCurrentStep();
  const {
    canScheduleWithContentCrmObject,
    contentObjectType
  } = useContentCrmObject();
  const {
    scheduledPublishDate,
    scheduledPublishTime,
    setScheduledPublishDate,
    setScheduledPublishTime,
    scheduledPublishDateTime,
    scheduledUnpublishDate,
    scheduledUnpublishTime,
    setScheduledUnpublishDate,
    setScheduledUnpublishTime,
    scheduledUnpublishDateTime,
    pageExpiryRedirectUrl,
    pageExpiryRedirectId,
    setPageExpiryRedirectUrl,
    setPageExpiryRedirectId,
    unpublishExpiryUrl,
    setUnpublishExpiryUrl,
    dateTimeCopyOptions,
    minimumDateForUnpublish,
    minimumTimeForUnpublish
  } = useSchedulingOptions();
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const contentType = useSelector(getContentTypeSlug);
  const isPage = useSelector(getIsPage);
  const dispatch = useDispatch();
  const showSchedulingControls = currentStep[currentTabType] === SchedulingSteps.SCHEDULING || currentStep[currentTabType] === SchedulingSteps.RESCHEDULING;
  const handleScheduleButtonClick = () => {
    if (canScheduleWithContentCrmObject) {
      if (schedulePublishUnpublish && (scheduledPublishDateTime || scheduledUnpublishDateTime)) {
        if (allowBackdating && scheduledPublishDateTime && scheduledPublishDateTime < Date.now()) {
          publish(PublishTypes.publish, undefined, scheduledPublishDateTime);
        } else {
          // For scheduling publish for draft content
          schedulePublishUnpublish({
            schedulePublishDate: scheduledPublishDateTime,
            scheduleUnpublishDate: scheduledUnpublishDateTime,
            unpublishExpiryUrl
          });
        }
      }
      closeModal();
      return;
    }

    // FIXME: remove the following logic once we fully migrate pages and blog posts to CMP
    if (scheduledPublishDateTime) {
      dispatch(updatePublishImmediately(false));
      dispatch(updatePublishDate(scheduledPublishDateTime));
    }
    if (scheduledUnpublishDateTime) {
      dispatch(updateExpiryEnabled(true));
      dispatch(updateExpiryDate(scheduledUnpublishDateTime));
      dispatch(updateExpiryRedirectUrl(pageExpiryRedirectUrl, [], pageExpiryRedirectId));
    }
    publish(PublishTypes.schedule);
    closeModal();
  };
  const onExitSchedulingClick = () => {
    updateCurrentStep(currentTabType, currentStep[currentTabType] === SchedulingSteps.RESCHEDULING ? SchedulingSteps.SCHEDULED : SchedulingSteps.NOTHING_SCHEDULED);
  };
  const isScheduledButtonEnabled = useMemo(() => {
    if (currentTabType === 'schedulePublish') {
      return !!scheduledPublishDateTime;
    }
    if (currentTabType === 'scheduleUnpublish') {
      if (!isPage) {
        return !!scheduledUnpublishDateTime;
      }
      return !!(scheduledUnpublishDateTime && (pageExpiryRedirectUrl || pageExpiryRedirectId || unpublishExpiryUrl));
    }
    return false;
  }, [currentTabType, isPage, pageExpiryRedirectId, pageExpiryRedirectUrl, scheduledPublishDateTime, scheduledUnpublishDateTime, unpublishExpiryUrl]);
  return /*#__PURE__*/_jsxs(UIModal, {
    width: 550,
    "data-test-id": "scheduling-options-modal",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "baseSchedulingOptionsModal.header"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: closeModal
      })]
    }), /*#__PURE__*/_jsx(SchedulingTabs, {
      canScheduleUnpublish: canScheduleUnpublish,
      tabType: currentTabType,
      handleTabChange: setCurrentTabType,
      currentStep: currentStep,
      scheduledPublishDateTime: scheduledPublishDateTime
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(UICardWrapper, {
        compact: true,
        children: /*#__PURE__*/_jsxs(UICardSection, {
          children: [currentStep[currentTabType] === SchedulingSteps.NOTHING_SCHEDULED ? /*#__PURE__*/_jsx(NothingScheduledControls, {
            actionTypeBodyInfoKey: actionTypeBodyInfoKey[currentTabType],
            onScheduleClick: () => {
              updateCurrentStep(currentTabType, SchedulingSteps.SCHEDULING);
            }
          }) : null, showSchedulingControls ? currentTabType === 'schedulePublish' ? /*#__PURE__*/_jsx(SchedulingPublishControls, {
            scheduledPublishDate: scheduledPublishDate,
            setPublishDateDay: setScheduledPublishDate,
            publishDateTime: scheduledPublishTime,
            setPublishDateTime: setScheduledPublishTime,
            isReadOnlyMode: isReadOnlyMode,
            allowBackdating: allowBackdating,
            maxPublishDate: scheduledUnpublishDate,
            onExitSchedulingClick: onExitSchedulingClick
          }) : canScheduleUnpublish ? /*#__PURE__*/_jsx(SchedulingUnpublishControls, {
            scheduledUnpublishDate: scheduledUnpublishDate,
            setScheduledUnpublishDate: setScheduledUnpublishDate,
            scheduledUnpublishTime: scheduledUnpublishTime,
            setScheduledUnpublishTime: setScheduledUnpublishTime,
            isReadOnlyMode: isReadOnlyMode,
            onExitSchedulingClick: onExitSchedulingClick,
            minimumDateForUnpublish: minimumDateForUnpublish,
            minimumTimeForUnpublish: minimumTimeForUnpublish,
            pageExpiryRedirectUrl: pageExpiryRedirectUrl,
            pageExpiryRedirectId: pageExpiryRedirectId,
            setPageExpiryRedirectUrl: setPageExpiryRedirectUrl,
            setPageExpiryRedirectId: setPageExpiryRedirectId,
            unpublishExpiryUrl: unpublishExpiryUrl,
            setUnpublishExpiryUrl: setUnpublishExpiryUrl
          }) : null : null, currentStep[currentTabType] === SchedulingSteps.SCHEDULED ? /*#__PURE__*/_jsx(CurrentlyScheduledCardInfo, {
            actionTypeBodyInfoKey: actionTypeBodyInfoKey[currentTabType],
            dateTimeCopyOptions: dateTimeCopyOptions,
            onReschedulingClick: () => {
              updateCurrentStep(currentTabType, SchedulingSteps.RESCHEDULING);
            },
            openCancelScheduleConfirmModal: openCancelScheduleConfirmModal
          }) : null]
        })
      }), showSchedulingControls && (currentTabType === 'schedulePublish' ? scheduledPublishDateTime : scheduledUnpublishDateTime) ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `baseSchedulingOptionsModal.body.info.${contentObjectType || contentType}.${actionTypeBodyInfoKey[currentTabType]}`,
        options: dateTimeCopyOptions
      }) : null, currentStep[currentTabType] === SchedulingSteps.SCHEDULED && currentTabType === 'schedulePublish' && contentTypeHasSavedViews && dashboardUrl ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: canScheduleWithContentCrmObject ? `baseSchedulingOptionsModal.body.findScheduledContent.${contentObjectType}.${actionTypeBodyInfoKey[currentTabType]}_jsx` : `baseSchedulingOptionsModal.body.findScheduledContent.${contentType}_jsx`,
        options: {
          href: `${dashboardUrl}/all-scheduled`
        },
        elements: {
          UILink
        }
      }) : null]
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      children: showSchedulingControls ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          onClick: handleScheduleButtonClick,
          disabled: !isScheduledButtonEnabled,
          "data-test-id": "schedule-button",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: currentStep[currentTabType] === SchedulingSteps.RESCHEDULING ? 'baseSchedulingOptionsModal.footerButtons.reschedule' : 'baseSchedulingOptionsModal.footerButtons.schedule'
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: closeModal,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "baseSchedulingOptionsModal.footerButtons.cancel"
          })
        })]
      }) : null
    }), /*#__PURE__*/_jsx(NavMarker, {
      name: "PUBLISHING_TAB_RENDERED"
    })]
  });
};
export default BaseSchedulingOptionsModal;